/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React from 'react';
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { useBranding } from "../contexts/BrandingContext";
import { useNavigate } from 'react-router-dom';

const DemoTermsPage = () => {
  const { branding } = useBranding();
  const navigate = useNavigate();
  const contactEmail = branding.contactEmail || 'contact@example.com';

  return (
    <div
      className="min-h-screen flex flex-col items-center p-4"
      style={{
        backgroundColor: 'var(--color-background)',
        color: 'var(--color-text)'
      }}
    >
      <Card className="w-full max-w-3xl bg-white shadow-xl my-8">
        <CardHeader>
          <CardTitle className="text-2xl font-bold" style={{ color: 'var(--color-primary)' }}>
            Demo Terms of Use
          </CardTitle>
          <p className="text-sm text-gray-600 mt-1">
            Last updated: 2026-05-08
          </p>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none space-y-4">
          <section>
            <h3 className="font-semibold text-base">What this is</h3>
            <p>
              This is a public, evaluation-only deployment of the {branding.schoolName || 'Safety Testing Platform'}.
              It is provided so prospective customers, partners, and curious visitors can explore
              the product&rsquo;s features hands-on, without signing up.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-base">No warranty</h3>
            <p>
              The demo is provided &ldquo;as is&rdquo; without warranty of any kind, express or implied.
              The licensor is not liable for any damages arising from your use of the demo.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-base">Data you enter is not private</h3>
            <p>
              The demo database is wiped and reseeded nightly. Anything you create — accounts, tests,
              submissions, comments — will be visible to anyone else using the demo until the next
              reset, and then it will be gone. <strong>Do not enter real personal information,
              private data, or anything you wouldn&rsquo;t want strangers to see.</strong>
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-base">Acceptable use</h3>
            <p>
              You may use the demo to evaluate the product. You may not:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>attempt to break out of the demo environment or attack the underlying infrastructure;</li>
              <li>use the demo to send spam or impersonate real people;</li>
              <li>upload malicious files or content;</li>
              <li>scrape or systematically harvest content from the demo.</li>
            </ul>
            <p>
              The licensor reserves the right to suspend or terminate access at any time for any reason.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-base">No license granted</h3>
            <p>
              Using the demo does not grant you any license to the underlying software. The platform
              is proprietary; copying, modifying, or redistributing the source or any output is
              prohibited. Production use requires a separate license agreement.
            </p>
          </section>

          <section>
            <h3 className="font-semibold text-base">Want to license this for your organization?</h3>
            <p>
              Contact <a href={`mailto:${contactEmail}`} className="underline" style={{ color: 'var(--color-primary)' }}>{contactEmail}</a> to
              discuss pricing, deployment, customization, and support.
            </p>
          </section>

          <div className="pt-4">
            <Button onClick={() => navigate('/')} variant="outline">
              ← Back to demo
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DemoTermsPage;
